// Bridge between the web renderer and the Electron desktop agent.
// Pushes the user's JWT + backend URL into the main process so that
// screenshots/browsing events can be uploaded under the user's identity.
// No-op when not running inside Electron.

import { BACKEND_URL, IS_ELECTRON } from './config'
import type { ElectronAgent } from '../types/electron'

function getAgent(): ElectronAgent | null {
  if (!IS_ELECTRON) return null
  return window.electronAPI?.agent ?? null
}

// Effective backend URL the desktop agent should hit. The renderer's
// axios client already has it via BACKEND_URL — we forward the same one
// so dev sessions hit localhost:3001 and prod hits os.rx2go.ai.
function effectiveBackend(): string {
  // Renderer's BACKEND_URL is '' on web (relative paths) — agent needs an
  // absolute origin. Fall back to current location origin.
  return BACKEND_URL || window.location.origin
}

export async function pushAuthToDesktop(token: string | null, screenshotIntervalSec?: number) {
  const agent = getAgent()
  if (!agent) return
  try {
    await agent.setAuth({ token, backendUrl: effectiveBackend(), screenshotIntervalSec })
  } catch {
    // ignore — agent is non-critical
  }
}

export async function triggerDesktopScreenshot(): Promise<boolean> {
  const agent = getAgent()
  if (!agent) return false
  try {
    const r = await agent.captureNow()
    return !!r.ok
  } catch {
    return false
  }
}
